/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * Copyright (C) Vaulut B.V. - All Rights Reserved - 2019
 * Unauthorized copying of this file via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Sten Reijers <stenreijers@gmail.com>
 *
 */
'use strict';

import { assert } from './assert.js';
import { debugCreate } from './debug.js';
const debug = debugCreate({ namespace: 'gamora:utils:strand' });

/**
 * Strand function type
 * @public
 */
export type GamoraUtilsStrandFunction = (...args: any[]) => Promise<any>;

/**
 * Strand function unpromised return type
 */
export type GamoraUtilsStrandFunctionArgsType<
  T extends GamoraUtilsStrandFunction,
> = T extends (...args: infer R) => Promise<any> ? R : any;

/**
 * Strand function unpromised return type
 */
export type GamoraUtilsStrandFunctionReturnType<
  T extends GamoraUtilsStrandFunction,
> = T extends (...args: any) => Promise<infer R> ? R : any;

/**
 * @public
 */
export interface GamoraUtilsStrandQueueItem {
  func: GamoraUtilsStrandFunction;
  promise: GamoraUtilsStrandPromise<any>;
  args: any[];
  reject: (reason?: any) => void;
  resolve: (result: any) => void;
}

/**
 * @public
 */
export type GamoraUtilsStrandPromiseGenerateResult<T> = {
  promise: GamoraUtilsStrandPromise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (value: T | PromiseLike<T>) => void;
};

/**
 * @public
 */
export class GamoraUtilsStrandPromise<T> extends Promise<T> {
  protected _hasCatch = false;
  protected _hasThen = false;
  constructor(
    executor: (
      resolve: (value: T | PromiseLike<T>) => void,
      reject: (reason?: any) => void,
    ) => void,
  ) {
    super(executor);
  }

  /**
   * Has catch handler associated
   */
  get hasCatch() {
    return this._hasCatch;
  }

  /**
   * Has then handler associated
   */
  get hasThen() {
    return this._hasThen;
  }

  /**
   * Attaches a callback for only the rejection of the Promise.
   * @param onrejected The callback to execute when the Promise is rejected.
   * @returns A Promise for the completion of the callback.
   */
  override catch<TResult = never>(
    onrejected?:
      | ((reason: any) => TResult | PromiseLike<TResult>)
      | null
      | undefined,
  ): Promise<T | TResult> {
    this._hasCatch = true;
    return super.catch(onrejected);
  }

  /**
   * Attaches callbacks for the resolution and/or rejection of the Promise.
   * @param onfulfilled The callback to execute when the Promise is resolved.
   * @param onrejected The callback to execute when the Promise is rejected.
   * @returns A Promise for the completion of which ever callback is executed.
   */
  override then<TResult1 = T, TResult2 = never>(
    onfulfilled?:
      | ((value: T) => TResult1 | PromiseLike<TResult1>)
      | null
      | undefined,
    onrejected?:
      | ((reason: any) => TResult2 | PromiseLike<TResult2>)
      | null
      | undefined,
  ): Promise<TResult1 | TResult2> {
    this._hasThen = true;
    return super.then(onfulfilled, onrejected);
  }

  /**
   * Generates a custom promise
   * @returns
   */
  static Generate<T>(): GamoraUtilsStrandPromiseGenerateResult<T> {
    const out: GamoraUtilsStrandPromiseGenerateResult<T> =
      {} as GamoraUtilsStrandPromiseGenerateResult<T>;
    out.promise = new GamoraUtilsStrandPromise((resolve, reject) => {
      out.resolve = resolve;
      out.reject = reject;
    });
    return out;
  }

  /**
   * Custom tag for strand promises
   */
  override get [Symbol.toStringTag]() {
    return 'GamoraUtilsStrandPromise';
  }
}

/**
 * A promise strand
 *
 * Executes all promises posted to strand sequentially
 * @public
 */
export class GamoraUtilsStrand {
  private _started = false;
  private _queue: GamoraUtilsStrandQueueItem[] = [];
  private _running = false;

  /**
   * Expose started flag
   */
  get started() {
    return this._started;
  }

  /**
   * Ticker
   * @private
   */
  private _tick() {
    // Execute strand item
    if (
      this._started === true &&
      this._running === false &&
      this._queue.length > 0
    ) {
      // Get first item
      const item = this._queue.shift() as GamoraUtilsStrandQueueItem;

      // Strand is running
      this._running = true;

      // Executes f
      item.func(...item.args).then(
        (result) => {
          // Notify strand promise holder
          item.resolve(result);

          // Move to next
          this._running = false;
          this._tick();
        },
        (e) => {
          // Warn that the promise has no catch handler
          if (!item.promise.hasCatch) {
            debug.warn(
              '(_tick/warn): No explicit catch handler on strand promise.',
            );
          }

          // Notify strand promise holder
          item.reject(e);

          // Move to next
          this._running = false;
          this._tick();
        },
      );
    }
  }

  /**
   * Posts async handler to strand
   * @param func The function to post to strand
   * @param args The function arguments
   */
  post<TFunction extends GamoraUtilsStrandFunction>(
    func: TFunction,
    ...args: GamoraUtilsStrandFunctionArgsType<TFunction>
  ): GamoraUtilsStrandPromise<GamoraUtilsStrandFunctionReturnType<TFunction>> {
    assert(typeof func === 'function', 'not a AsyncFunction.');
    assert(func.constructor.name === 'AsyncFunction', 'not a AsyncFunction.');
    assert(Array.isArray(args), 'Invalid args argument: not an Array.');

    // Create promise
    const promiseGenerateResult =
      GamoraUtilsStrandPromise.Generate<
        GamoraUtilsStrandFunctionReturnType<TFunction>
      >();

    // Queue promise
    this._queue.push({
      func: func,
      args: args,
      reject: promiseGenerateResult.reject,
      resolve: promiseGenerateResult.resolve,
      promise: promiseGenerateResult.promise,
    });

    // Notify ticker
    queueMicrotask(() => {
      this._tick();
    });

    // Returns promise
    return promiseGenerateResult.promise;
  }

  /**
   * Posts priority async handler to strand
   * @param func The function to post to strand
   * @param args The function arguments
   */
  post_priority<TFunction extends GamoraUtilsStrandFunction>(
    func: TFunction,
    ...args: GamoraUtilsStrandFunctionArgsType<TFunction>
  ): GamoraUtilsStrandPromise<GamoraUtilsStrandFunctionReturnType<TFunction>> {
    assert(typeof func === 'function', 'not a AsyncFunction.');
    assert(func.constructor.name === 'AsyncFunction', 'not a AsyncFunction.');
    assert(Array.isArray(args), 'Invalid args argument: not an Array.');

    // Create promise
    const promiseGenerateResult =
      GamoraUtilsStrandPromise.Generate<
        GamoraUtilsStrandFunctionReturnType<TFunction>
      >();

    // Queue promise
    this._queue.unshift({
      func: func,
      args: args,
      reject: promiseGenerateResult.reject,
      resolve: promiseGenerateResult.resolve,
      promise: promiseGenerateResult.promise,
    });

    // Notify ticker
    queueMicrotask(() => {
      this._tick();
    });

    // Returns promise
    return promiseGenerateResult.promise;
  }

  /**
   * Synchronizes with strand
   */
  sync(): GamoraUtilsStrandPromise<void> {
    return this.post(async () => {
      return;
    });
  }

  /**
   * Start the strand
   */
  start(): void {
    assert(this._started === false, 'Invalid start state: already started.');
    this._started = true;
    this._tick();
  }

  /**
   * Stop the strand
   */
  stop(): void {
    assert(this._started === true, 'Invalid start state: not yet started.');
    this._started = false;
  }
}
